import {
    Component,
    EventEmitter,
    ViewChild,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    OnChanges,
    Output,
    SimpleChanges,
} from "@angular/core";
import { ValueList } from "#models/valueList.interface";
import { UsherPayloadHelper } from "#core/helpers";
@Component({
    selector: "value-selector",
    templateUrl: "./value-selector.component.html",
    styleUrls: ["./value-selector.component.scss"],
    standalone: false
})
export class ValueSelectorComponent implements OnInit, OnChanges {
    elementId: string =
        "valueSelector" +
        (Math.random() * 100000).toFixed(0) +
        "_" +
        (Math.random() * 100000).toFixed(0);
    maxHeight = 500;
    extendDown = true;
    @Input() selectingValueOptions: boolean;
    @Input() selectedID: number | string | [];
    @Input() valueList: ValueList[];
    @Input() placeholderValue: string;
    @Input() setWidth: string;
    @Input() maxWidth: string;
    @Input() currentSelection: ValueList;
    @Input() savedCurrentSelection: ValueList;
    @Input() disabled: boolean;
    @Input() valueSelectorID: string;
    @Input() emissionType: string | undefined;
    @Input() alignOptionsRight?: boolean;
    @Input() inlineValueSelector?: boolean;
    @Input() setHeight?: string;
    @Input() usedInReportsFilter = false;
    @Input() warningMessage: string | null = null;

    @Output() currentSelectionChange = new EventEmitter<number | string>();
    @Output() focusLost = new EventEmitter<boolean>(); // focus gained?
    @ViewChild("valueSelectContainer") private valueSelectContainer: ElementRef;

    constructor(private usherPayloadHelper: UsherPayloadHelper) {}

    @HostListener("document:click", ["$event"])
    onClick(event: MouseEvent) {
        if (!this.valueSelectContainer.nativeElement.contains(event.target)) {
            this.selectingValueOptions = false; // if the click didn't emanate from within this element, close it.
        }
    }

    ngOnInit(): void {
        this.setSelectedSelection();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.setSelectedSelection();
        if (
            changes.selectedID?.previousValue !==
            changes.selectedID?.currentValue
        ) {
            const foundOption = this.valueList.find(
                (t) => t.id === changes.selectedID.currentValue
            );
            if (foundOption) {
                this.currentSelection = foundOption;
            }
        }
    }

    setSelectedSelection() {
        const selectedSelection = this.valueList?.find(
            (x) => x.id === this.selectedID
        );
        if (selectedSelection) {
            this.currentSelection = selectedSelection;
        }
        this.savedCurrentSelection = this.currentSelection;
        this.usherPayloadHelper.setDataForContentLibrary(this.currentSelection);
    }

    setSelectingValueOptions() {
        this.selectingValueOptions = !this.selectingValueOptions;
        this.selectSizer();
    }

    setCurrentSelection(
        option: ValueList,
        index: number,
        closeBypass: boolean
    ) {
        this.currentSelection = option;
        this.savedCurrentSelection = option;
        this.selectedID = option.id;
        if (this.emissionType === "id") {
            this.currentSelectionChange.emit(option.id);
        } else {
            this.currentSelectionChange.emit(index);
        }
        if (!closeBypass) {
            this.selectingValueOptions = false;
        }
    }

    getCurrentSelectionName(): string | undefined {
        if (this.currentSelection) {
            return this.currentSelection.value;
        } else if (this.savedCurrentSelection) {
            return this.savedCurrentSelection.value;
        } else if (this.valueList) {
            const selection = this.valueList.find(
                (x) => x.id === this.selectedID
            );
            return selection ? selection.value : this.placeholderValue;
        } else {
            return this.placeholderValue;
        }
    }

    showOptions() {
        if (this.selectingValueOptions && !this.disabled) {
            return true;
        }
    }

    keyHandler(event: KeyboardEvent): void {
        const keyCode: string = event.code.toLowerCase();
        if (!this.disabled) {
            if (this.selectingValueOptions) {
                const options: HTMLCollection =
                    this.valueSelectContainer.nativeElement.getElementsByClassName(
                        "value-selector-option"
                    );
                let selectedIndex = 0,
                    moveInc = 0,
                    newIndex = 0;
                if (["tab", "escape", "enter", "space"].includes(keyCode)) {
                    this.selectingValueOptions = false;
                    this.focusLost.emit(true); // tabbed away or esc, or selected with space or enter...just close it and emit
                } else if (
                    ["arrowdown", "arrowup", "home", "end"].includes(keyCode) &&
                    this.valueList.length
                ) {
                    event.preventDefault(); // prevent containing page from being scrolled by arrows, home, or end
                    if (["home", "end"].includes(keyCode)) {
                        newIndex =
                            keyCode === "home" ? 0 : this.valueList.length - 1;
                    } else {
                        selectedIndex = !this.currentSelection
                            ? 0
                            : this.valueList.findIndex(
                                  (vl) => vl.id === this.currentSelection.id
                              );
                        moveInc = keyCode === "arrowup" ? -1 : 1;
                        if (
                            selectedIndex + moveInc <=
                                this.valueList.length - 1 &&
                            selectedIndex + moveInc >= 0
                        ) {
                            newIndex = selectedIndex + moveInc;
                        } else if (
                            selectedIndex + moveInc >
                            this.valueList.length - 1
                        ) {
                            newIndex = 0;
                        } else if (selectedIndex + moveInc < 0) {
                            newIndex = this.valueList.length - 1;
                        }
                    }
                    this.setCurrentSelection(
                        this.valueList[newIndex],
                        newIndex,
                        true
                    ); // selection happens here, no need to do it later on enter or space
                    this.scrollToOption(options[newIndex + 1]);
                } else if (
                    [
                        "a",
                        "b",
                        "c",
                        "d",
                        "e",
                        "f",
                        "g",
                        "h",
                        "i",
                        "j",
                        "k",
                        "l",
                        "m",
                        "n",
                        "o",
                        "p",
                        "q",
                        "r",
                        "s",
                        "t",
                        "u",
                        "v",
                        "w",
                        "x",
                        "y",
                        "z",
                    ].includes(event.key) &&
                    this.valueList.length
                ) {
                    newIndex = this.valueList.findIndex(
                        (vl) => vl.value.toLowerCase().indexOf(event.key) === 0
                    );
                    // TODO: Make this more robust? For now, only care about the first letter. Making this catch longer strings, will entail acrobatics involving a timeout.
                    if (newIndex > 0) {
                        this.setCurrentSelection(
                            this.valueList[newIndex],
                            newIndex,
                            true
                        ); // selection happens here, no need to do it later on enter or space
                        this.scrollToOption(options[newIndex + 1]);
                    }
                }
            } else {
                if (["enter", "space"].includes(keyCode)) {
                    this.selectingValueOptions = true; // just open it
                    this.selectSizer();
                } else if (keyCode === "tab") {
                    this.focusLost.emit(true); // tabbed away without even opening it, emit...
                }
            }
        }
    }

    selectSizer(): void {
        let containerHeight: number, testHeight: number;
        const elem = this.valueSelectContainer.nativeElement,
            boundingRect: DOMRect = elem.getBoundingClientRect(),
            winHeight: number =
                window.innerHeight && window.innerHeight < window.screen.height
                    ? window.innerHeight
                    : window.screen.height, // use whichever is smaller
            topPos: number = boundingRect.y ? boundingRect.y : boundingRect.top;
        containerHeight =
            (boundingRect.height ? boundingRect.height : elem.offsetHeight) +
            10; // 10 more pixels for a little breathing room
        testHeight =
            Math.floor(winHeight) -
            Math.ceil(topPos) -
            Math.ceil(containerHeight);
        if (testHeight < 200) {
            this.extendDown = false;
            this.maxHeight = topPos - containerHeight;
        } else {
            this.extendDown = true;
            this.maxHeight = testHeight;
        }
    }

    scrollToOption(element: Element): void {
        element.scrollIntoView();
    }

    maxHeightReturn(): { [index: string]: string } {
        return { "max-height": this.maxHeight + "px" };
    }
}
